import React, { useState, useEffect, useRef } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import makeStyles from '@material-ui/core/styles/makeStyles'
import moment from 'moment'

import { Editing, LoadingButton, StepHint } from '../../../../../components'
import { errors as errorUtils, dates } from '../../../../../utils'
import { Main } from '../../../../../components/Layout'
import { Buttons } from '../../../../../components/Steps'
import { intentService, appService } from '../../../../../services'

import Agreed from './elements/Agreed'
import General from './elements/General'
import Options from './elements/Options'

const { parsePtBr } = dates
const { includeError } = errorUtils

const useStyles = makeStyles(() => ({
  suggestion: {
    marginTop: '4px',
  },
  error: {
    marginTop: '16px',
  },
  suggestionDivider: {
    margin: '20px 0 10px',
  },
  divider: {
    marginTop: '20px',
  },
  ul: {
    margin: '8px 0 0',
    paddingLeft: '24px',
  },
}))

const Intent = ({ data, errors, persist, prev, validating }) => {
  const classes = useStyles()

  const { address } = data

  const [optTwo, optThree] = [useRef(), useRef()]
  const [degreeOpened, setDegreeOpened] = useState('')

  const [count, setCount] = useState(0)
  const [degrees, setDegrees] = useState([])
  const [intent, setIntent] = useState({ ...data.intent })
  const [agreed, setAgreed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [otherCurriculums, setOtherCurriculums] = useState([])
  const [warning, setWarning] = useState(false)

  const tryToDetectDegreeRef = () => {
    try {
      return intent.optOne.degreeRef
    } catch {
      return undefined
    }
  }

  const [degreeRef, setDegreeRef] = useState(tryToDetectDegreeRef())

  const exceptOfferIds = () =>
    [intent.optOne.offerId, intent.optTwo.offerId]
      .filter(i => !isNaN(i))
      .join(',')

  const cityRefs = () =>
    [...new Set([address.cityRef, intent.optOne.cityRef])]
      .filter(i => !isNaN(i))
      .join(',')

  const filled = () =>
    [
      intent.optOne.completed,
      intent.optTwo.completed,
      intent.optThree.completed,
    ].filter(Boolean).length

  const suggested = () =>
    degrees
      .sort((a, b) => (moment(a.from).isBefore(b.from) ? 1 : -1))
      .find(
        i =>
          parsePtBr(data.student.birthdate).isSameOrAfter(moment(i.from)) &&
          parsePtBr(data.student.birthdate).isSameOrBefore(moment(i.to))
      )

  const tryToGetAvailableDegrees = async () => {
    setLoading(true)

    const {
      success,
      payload: { degrees: _degrees },
    } = await intentService.getAllPublicCallDegrees()

    if (success) {
      setDegrees(_degrees)
    }

    setLoading(false)
  }

  const tryToCheckOtherCurriculums = async () => {
    setLoading(true)

    const {
      success,
      payload: { curriculums: _curriculums },
    } = await intentService.getOtherAvailableCurriculums(
      intent.optOne.degreeRef,
      cityRefs(),
      exceptOfferIds()
    )

    if (success) {
      setOtherCurriculums(_curriculums)
    } else {
      setOtherCurriculums([])
    }
  }

  const tryToSubmit = async () => {
    if (filled() < 3) {
      return await tryToCheckOtherCurriculums()
    }

    persist({ intent })()
  }

  const update = attr => {
    setIntent({ ...intent, ...attr })
  }

  const shouldDisableNextButton = () => {
    try {
      return !intent.optOne.completed || !agreed || validating || loading
    } catch {
      return true
    }
  }

  useEffect(() => {
    tryToGetAvailableDegrees()
  }, [])

  useEffect(() => {
    if (includeError(errors)('intent')) {
      setWarning(false)
      window.scrollTo({ top: 215, behavior: 'smooth' })
    }
  }, [errors])

  useEffect(() => {
    if (!appService.getLocalPublicCall().suggestDegreeByAge) return

    const found = suggested()

    if (found && !degreeRef) {
      setDegreeRef(found.value)

      setIntent({
        ...intent,
        optOne: {
          degreeRef: found.value,
          degreeName: found.label,
          levelRef: found.levelRef,
          levelName: found.levelName,
        },
      })
    }
  }, [degrees])

  /*
    workaround por conta de não ter um callback para garantir o update e o
    useEffect executar logo no primeiro render
  */

  useEffect(() => {
    if (count > 0) {
      if (otherCurriculums.length > 0 && filled() < 3) {
        setLoading(false)
        setWarning(true)
      } else {
        persist({ intent })()
      }
    } else {
      setCount(count + 1)
    }
  }, [otherCurriculums])

  const handleClickClose = () => {
    if (filled() === 1) {
      optTwo.current.scrollIntoView({ behavior: 'smooth' })
      setDegreeOpened('two')
    } else {
      optThree.current.scrollIntoView({ behavior: 'smooth' })
      setDegreeOpened('three')
    }

    setWarning(false)
  }

  return (
    <>
      <StepHint>
        Agora é hora de escolher a(s) opção(ões) de escola e oferta
      </StepHint>

      <Main>
        <Editing />

        {appService.getLocalPublicCall().suggestDegreeByAge && (
          <General
            classes={classes}
            degrees={degrees}
            errors={errors}
            intent={intent}
            loading={loading}
            setLoading={setLoading}
            validating={validating}
            student={data.student}
            suggested={suggested}
            update={update}
          />
        )}

        <Options
          degrees={degrees}
          errors={errors}
          exceptOfferIds={exceptOfferIds()}
          intent={intent}
          loading={loading}
          validating={validating}
          setLoading={setLoading}
          update={update}
          zipcode={data.address.zipcode}
          weakStreetId={data.address.weakStreetId}
          optRefs={{ optTwo, optThree }}
          degreeOpened={degreeOpened}
          setDegreeOpened={setDegreeOpened}
        />

        <Divider className={classes.divider} />

        <Agreed
          agreed={agreed}
          loading={loading}
          validating={validating}
          setAgreed={setAgreed}
        />
      </Main>

      <Buttons>
        <Button variant="contained" color="primary" onClick={prev}>
          Voltar
        </Button>

        <LoadingButton
          disabled={shouldDisableNextButton()}
          loading={(validating || loading) && !warning}
          onClick={tryToSubmit}
        >
          Próxima
        </LoadingButton>
      </Buttons>

      <Dialog open={warning} onClose={handleClickClose}>
        <DialogTitle>Deseja prosseguir com a inscrição?</DialogTitle>

        <DialogContent dividers>
          <DialogContentText>
            Existem outras opções de escola para o ano de escolaridade
            selecionado. Recomendamos que indique as três opções de escolas a
            que tem direito para aumentar as chances de ser selecionado em uma
            de sua preferência.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClickClose}>Adicionar mais opções</Button>

          <LoadingButton
            disabled={shouldDisableNextButton()}
            loading={validating}
            onClick={persist({ intent })}
          >
            {filled() > 1
              ? 'Continuar com apenas duas opções'
              : 'Continuar com apenas a 1ª opção'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Intent
